import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Boxes, Lightbulb, ClipboardList, Truck, Package,
  Warehouse, FileBarChart, Settings, Sun, Moon, ChevronDown, Maximize2, Minimize2, RefreshCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/contexts/ThemeContext";
import { useRole } from "@/contexts/RoleContext";
import { api } from "@/lib/api";
import { SyncNotice } from "./SyncNotice";
import { WebshopPill } from "./WebshopPill";
import { useWebshopStatus } from "@/lib/useWebshopStatus";
import { BrandLogo } from "./BrandLogo";
import { WarehouseSwitch } from "./WarehouseSwitch";
import { useWarehouse } from "@/contexts/WarehouseContext";
import { Footer } from "./Footer";
import { SignOutButton } from "./SignOutButton";
import { cn } from "@/lib/utils";
import { formatRelative } from "@/lib/format";

const NAV = [
  { to: "/", label: "Overzicht", icon: LayoutDashboard, roles: ["hubert", "rodilene", "magazijn", "admin"], testId: "nav-overview" },
  { to: "/voorraad", label: "Voorraad", icon: Boxes, roles: ["hubert", "rodilene", "magazijn", "admin"], testId: "nav-inventory" },
  { to: "/inkoopadvies", label: "Inkoopadvies", icon: Lightbulb, roles: ["hubert", "rodilene", "admin"], testId: "nav-advice" },
  { to: "/bestellingen", label: "Bestellingen", icon: ClipboardList, roles: ["hubert", "rodilene", "admin"], testId: "nav-orders" },
  { to: "/leveringen", label: "Leveringen", icon: Truck, roles: ["magazijn", "rodilene", "admin"], testId: "nav-deliveries" },
  { to: "/producten", label: "Producten", icon: Package, roles: ["hubert", "rodilene", "magazijn", "admin"], testId: "nav-products" },
  { to: "/magazijnen", label: "Magazijnen", icon: Warehouse, roles: ["hubert", "rodilene", "magazijn", "admin"], testId: "nav-warehouses" },
  { to: "/rapportages", label: "Rapportages", icon: FileBarChart, roles: ["hubert", "rodilene", "admin"], testId: "nav-reports" },
  { to: "/instellingen", label: "Instellingen", icon: Settings, roles: ["admin"], testId: "nav-settings" },
];

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <Button data-testid="theme-toggle" variant="outline" size="icon" onClick={toggle}
      className="h-11 w-11 rounded-md" aria-label="Wissel thema">
      {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </Button>
  );
}

function FullscreenToggle() {
  const [fs, setFs] = useState(false);
  useEffect(() => {
    const onChange = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggle = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => {});
  };
  return (
    <Button data-testid="fullscreen-toggle" variant="outline" size="icon" onClick={toggle}
      className="h-11 w-11 rounded-md hidden lg:inline-flex" aria-label="Volledig scherm">
      {fs ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
    </Button>
  );
}

function ExactPill() {
  const [exact, setExact] = useState(null);
  useEffect(() => { api.get("/exact/status").then((r) => setExact(r.data)).catch(() => {}); }, []);
  if (!exact) return null;
  const connected = exact.connected;
  const toneCls = connected
    ? "border-status-healthy/40 text-status-healthy bg-status-healthy/10"
    : "border-status-low/50 text-status-low bg-status-low/10";
  return (
    <Link to="/instellingen" data-testid="exact-status-pill" title={exact.message}
      className={cn("hidden xl:flex items-center gap-2 px-3 h-11 rounded-md border text-xs font-medium uppercase tracking-wider", toneCls)}>
      <RefreshCcw className="w-4 h-4" />
      <div className="flex flex-col leading-tight">
        <span>Exact {connected ? "verbonden" : "offline"}</span>
        <span className="opacity-70 normal-case tracking-normal font-normal">
          Sync: {exact.last_sync ? formatRelative(exact.last_sync) : "nog niet"}
        </span>
      </div>
    </Link>
  );
}

function RoleSwitcher() {
  const { role, setRole, roles } = useRole();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button data-testid="role-switcher" variant="outline" aria-label={`Rol: ${role.short}`} title={`Rol: ${role.short}`} className="h-11 px-4 gap-2 rounded-md font-medium">
          <span className="inline-block w-2 h-2 rounded-full bg-primary" />
          <span className="hidden sm:inline">{role.short}</span>
          <ChevronDown className="w-4 h-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Wissel gebruikersrol</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={role.id} onValueChange={setRole}>
          {roles.map((r) => (
            <DropdownMenuRadioItem key={r.id} value={r.id} data-testid={`role-option-${r.id}`}
              className="flex flex-col items-start py-3">
              <span className="font-medium">{r.name}</span>
              <span className="text-xs text-muted-foreground">{r.description}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <Link to="/login" data-testid="goto-login">
          <div className="px-2 py-2 text-sm hover:bg-accent rounded-sm cursor-pointer">Naar inlogscherm</div>
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { role } = useRole();
  const location = useLocation();
  const { warehouse } = useWarehouse();
  const isHome = location.pathname === "/";
  const visible = NAV.filter((n) => n.roles.includes(role.id));
  const { status: webshop } = useWebshopStatus();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header data-testid="top-bar" className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="h-1 bg-primary" />
        <div className="flex items-center justify-between gap-4 px-4 md:px-8 h-24 md:h-28 lg:h-32">
          <Link to="/" className="flex items-center gap-4 md:gap-5 min-w-0" data-testid="brand-home">
            <BrandLogo variant="full" trimmed={isHome} className={cn("w-auto shrink-0 transition-[height] duration-300", isHome ? "h-16 md:h-20 lg:h-24" : "h-11 md:h-14 lg:h-16 xl:h-[68px]")} />
            <div className="hidden md:block border-l border-border pl-4 md:pl-5 min-w-0">
              <div className="text-lg md:text-xl lg:text-2xl font-semibold tracking-tight truncate">Voorraad & Inkoop</div>
              <div className="text-xs md:text-sm text-muted-foreground truncate">
                Actueel overzicht vanuit kipkruiden.nl
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <WebshopPill status={webshop} />
            <ExactPill />
            <RoleSwitcher />
            <ThemeToggle />
            <FullscreenToggle />
            <SignOutButton />
          </div>
        </div>
        <SyncNotice status={webshop} />
        <WarehouseSwitch />
      </header>

      <div className="flex-1 flex">
        <aside data-testid="sidebar" className="hidden md:flex flex-col w-64 lg:w-72 border-r border-border bg-card/40 shrink-0">
          <nav className="flex-1 p-4 lg:p-6 space-y-1">
            {visible.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} data-testid={item.testId}
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-4 py-4 rounded-md text-base font-medium transition-colors min-h-[56px]",
                  isActive
                    ? "bg-primary/10 text-primary border border-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}>
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="p-4 lg:p-6 border-t border-border text-xs text-muted-foreground">
            <div className="font-mono uppercase tracking-widest">Voorraad · Inkoop · Magazijn</div>
            <div className="mt-1">Fase 1 · Live webshop-koppeling</div>
          </div>
        </aside>

        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border overflow-x-auto scrollbar-thin">
          <div className="flex">
            {visible.slice(0, 5).map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"}
                data-testid={`${item.testId}-mobile`}
                className={({ isActive }) => cn(
                  "flex-1 flex flex-col items-center gap-1 py-3 px-2 text-[10px] uppercase tracking-wider font-medium",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}>
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col">
          <main key={`${location.pathname}-${warehouse}`} className="flex-1 min-w-0 animate-fade-in pb-24 md:pb-0">
            {children}
          </main>
          <Footer />
        </div>
      </div>
    </div>
  );
}
